import { useState, useEffect, useCallback, useRef } from 'react';
import { secureStorage, privacy } from '../utils/encryption';
import { apiService } from '../utils/api';
import { trackTaskComplete } from '../utils/analytics';
import type { UnifiedContact } from '../services/unifiedContactService';
import { unifiedContactService } from '../services/unifiedContactService';
import type { FlowId } from '../utils/frameFlowMap';
import { FLOW_COMPLETION_CONFIG } from '../utils/flowCompletionConfig';


// Types for session data
export interface UserSession {
  id: string;
  email?: string;
  name?: string;
  phone?: string;
  roles?: string[];
  notifications?: {
    email: boolean;
    inApp: boolean;
    sms: boolean;
  };
  accountId?: string; // Link to user account
  accountToken?: string; // Authentication token
  currentStep: 'landing' | 'onboarding' | 'dashboard' | 'questions';
  completedTasks: string[];
  taskProgress: Record<string, {
    status: 'not-started' | 'in-progress' | 'complete';
    answers: Record<string, unknown>;
    lastUpdated: string;
    completed?: boolean;
    collectionsComplete?: boolean;
    completionMeta?: {
      confirmedEmpty?: boolean;
      completedAt?: string;
      source?: 'FinishFlowAction' | string;
      flowId?: string;
      // allow extension without TS fights:
      [k: string]: unknown;
    };  
  }>;
  onboardingComplete: boolean;
  lastSaved: string;
  sessionStarted: string;
  lastActivity: string; // Track last user activity
  sectionStartTimes: Record<string, string>; // Track when sections were started
  transmissionStatus: Record<string, boolean>; // Track which sections have been transmitted
  contacts?: UnifiedContact[]; // Unified contact list (synced with backend)
  memoryCollectionsComplete?: boolean;
}

export type RequestCompleteTaskOptions = {
  confirmEmpty?: boolean;
  meta?: UserSession['taskProgress'][string]['completionMeta'] & {
    // strongly suggested additions for flow completion semantics
    completionKind?: 'with_data' | 'empty_flow' | 'empty_completion_task';
    completedWithData?: boolean;
  };
};


// Session configuration
const SESSION_CONFIG = {
  MAX_IDLE_TIME: 1800 * 1000,      // 30 minutes
  WARNING_TIME: 1500 * 1000,       // warning with 5 minutes left, so at ~25 mins
  MAX_SESSION_TIME: 1800 * 1000,
} as const;

// Default session state
const createDefaultSession = (): UserSession => ({
  id: crypto.randomUUID(),
  currentStep: 'landing',
  completedTasks: [],
  taskProgress: {},
  onboardingComplete: false,
  lastSaved: new Date().toISOString(),
  sessionStarted: new Date().toISOString(),
  sectionStartTimes: {},
  transmissionStatus: {},
  lastActivity: new Date().toISOString()
});

// Session storage key (will be prefixed with 'estate-plan-' by secureStorage)
const SESSION_STORAGE_KEY = 'session';

export const useSessionPersistence = () => {
  const [session, setSession] = useState<UserSession>(() => createDefaultSession());
  const [isLoading, setIsLoading] = useState(true);
  const [encryptionStatus, setEncryptionStatus] = useState<{
    encrypted: boolean;
    private: boolean;
    secure: boolean;
  }>({ encrypted: false, private: false, secure: false });
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);
  const [warningTimeRemainingMs, setWarningTimeRemainingMs] = useState<number>(SESSION_CONFIG.WARNING_TIME);
  const [syncStatus, setSyncStatus] = useState<{
    lastSync: string | null;
    synced: boolean;
    localSections: string[];
    backendSections: string[];
    missingSections: string[];
    outdatedSections: string[];
  }>({
    lastSync: null,
    synced: false,
    localSections: [],
    backendSections: [],
    missingSections: [],
    outdatedSections: []
  });

  // FIX: Use ref to store current session for auto-save to prevent race conditions
  const sessionRef = useRef<UserSession>(session);
  const warningTimerRef = useRef<number | null>(null);
  const expirationTimerRef = useRef<number | null>(null);
  const absoluteSessionTimerRef = useRef<number | null>(null);

  const clearSessionTimers = useCallback(() => {
    if (warningTimerRef.current) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    if (expirationTimerRef.current) {
      window.clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }

    if (absoluteSessionTimerRef.current) {
      window.clearTimeout(absoluteSessionTimerRef.current);
      absoluteSessionTimerRef.current = null;
    }
  }, []);

  const hasMeaningfulValue = (v: unknown): boolean => {
    if (v == null) return false;

    // strings
    if (typeof v === 'string') return v.trim().length > 0;

    // numbers/booleans: treat as meaningful if present
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'boolean') return v === true;


    // arrays: meaningful if any element meaningful
    if (Array.isArray(v)) return v.some(hasMeaningfulValue);

    // objects: meaningful if any key has meaningful value
    if (typeof v === 'object') return Object.values(v).some(hasMeaningfulValue);

    return false;
  };

  
  // Update ref whenever session changes
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Listen for session updates from other hook instances (e.g. login in LoginPage
  // needs to notify App's instance so the expiration timers can start).
  useEffect(() => {
    const handleSessionSync = async () => {
      try {
        const saved = await secureStorage.getItem(SESSION_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.id && parsed.sessionStarted) {
            console.log('[SessionPersistence] Syncing session from storage (cross-instance update)');
            setSession(parsed);
          }
        }
      } catch (error) {
        console.error('[SessionPersistence] Failed to sync session from storage:', error);
      }
    };

    window.addEventListener('session-updated', handleSessionSync);
    return () => window.removeEventListener('session-updated', handleSessionSync);
  }, []);

  // Initialize session from encrypted storage with cross-device sync
  useEffect(() => {
    const initializeSession = async () => {
      setIsLoading(true);
      
      console.log('[SessionPersistence] Initializing session with cross-device sync...');
      
      // Check encryption status
      const storageInfo = privacy.getStorageInfo();
      setEncryptionStatus(storageInfo);
      console.log('[SessionPersistence] Storage info:', storageInfo);
      
      try {
        console.log('[SessionPersistence] Attempting to load session from storage...');
        // Use secure encrypted storage
        const savedSession = await secureStorage.getItem(SESSION_STORAGE_KEY);
        console.log('[SessionPersistence] Raw saved session:', savedSession ? 'exists' : 'null');
        
        let localSession: UserSession;
        
        if (savedSession) {
          const parsed = JSON.parse(savedSession);
          console.log('[SessionPersistence] ============== SESSION INITIALIZATION ==============');
          console.log('[SessionPersistence] Parsed session structure:', {
            hasId: !!parsed.id,
            hasSessionStarted: !!parsed.sessionStarted,
            completedTasks: parsed.completedTasks?.length || 0,
            completedTasksList: parsed.completedTasks || [],
            taskProgressKeys: Object.keys(parsed.taskProgress || {}),
            sessionId: parsed.id
          });
          
          // Validate session structure
          if (parsed.id && parsed.sessionStarted) {
            localSession = parsed;
            console.log('[SessionPersistence] Session restored successfully with encryption');
            console.log('[SessionPersistence] RESTORED completedTasks:', localSession.completedTasks);
          } else {
            console.warn('[SessionPersistence] Invalid session structure, creating new session');
            localSession = createDefaultSession();
          }
        } else {
          console.log('[SessionPersistence] No existing session found, creating new session');
          localSession = createDefaultSession();
        }

        // Perform cross-device data synchronization
        console.log('[SessionPersistence] Starting cross-device data synchronization...');
        let reconciledSession = localSession;
        
        // Only attempt API sync if user has account token
        const accountToken = localStorage.getItem('account-token');
        if (accountToken) { // ENABLED: API calls for server synchronization
          try {
            reconciledSession = await apiService.reconcileUserData(localSession);
            console.log('[SessionPersistence] Cross-device sync completed successfully');
          } catch (error) {
            console.warn('[SessionPersistence] Cross-device sync failed, using local session:', error);
            reconciledSession = localSession;
          }
        } else {
          console.log('[SessionPersistence] No account token, skipping cross-device sync');
        }
        
        // Verify sync status (optional)
        let syncVerification = {
          synced: true,
          localSections: Object.keys(reconciledSession.taskProgress || {}),
          backendSections: [] as string[],
          missingSections: [] as string[],
          outdatedSections: [] as string[]
        };
        
        if (accountToken) { // ENABLED: API calls for sync verification
          try {
            syncVerification = await apiService.verifyDataSync(reconciledSession);
          } catch (error) {
            console.warn('[SessionPersistence] Sync verification failed:', error);
          }
        }

        setSyncStatus({
          lastSync: new Date().toISOString(),
          synced: syncVerification.synced,
          localSections: syncVerification.localSections,
          backendSections: syncVerification.backendSections,
          missingSections: syncVerification.missingSections,
          outdatedSections: syncVerification.outdatedSections
        });

        console.log('[SessionPersistence] Session initialization complete:', {
          sessionId: reconciledSession.id,
          hasTaskProgress: Object.keys(reconciledSession.taskProgress || {}).length > 0,
          completedTasks: reconciledSession.completedTasks?.length || 0,
          synced: syncVerification.synced
        });

        // Save reconciled session back to encrypted storage
        try {
          await secureStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(reconciledSession));
          console.log('[SessionPersistence] Reconciled session saved to encrypted storage');
        } catch (saveError) {
          console.error('[SessionPersistence] Failed to save reconciled session:', saveError);
        }

        // Use reconciled session
        setSession(reconciledSession);
        
      } catch (error) {
        console.error('[SessionPersistence] Failed to initialize session:', error);
        console.warn('[SessionPersistence] Creating new session due to initialization error');
        setSession(createDefaultSession());
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, []);

  // Auto-save session whenever it changes (with encryption) - DEBOUNCED
  useEffect(() => {
    if (isLoading) return; // Don't save during initial load
    
    // Debounce saves to prevent excessive calls
    const saveTimer = setTimeout(async () => {
      // FIX: Use sessionRef.current to get the most recent session state
      const currentSession = sessionRef.current;
      
      console.log('[SessionPersistence] ============== AUTO-SAVE TRIGGERED ==============');
      console.log('[SessionPersistence] Debounced auto-save triggered...', {
        sessionId: currentSession.id,
        completedTasks: currentSession.completedTasks.length,
        taskProgressKeys: Object.keys(currentSession.taskProgress),
        lastSaved: currentSession.lastSaved,
        completedTasksList: currentSession.completedTasks
      });
      
      // Check what's currently in storage before overwriting (use secureStorage to properly decrypt)
      try {
        const beforeStorage = await secureStorage.getItem(SESSION_STORAGE_KEY);
        if (beforeStorage) {
          const beforeParsed = JSON.parse(beforeStorage);
          console.log('[SessionPersistence] AUTO-SAVE BEFORE - storage completedTasks:', beforeParsed.completedTasks?.length || 0);
          console.log('[SessionPersistence] AUTO-SAVE BEFORE - storage lastSaved:', beforeParsed.lastSaved);
        }
      } catch {
        // Ignore parse errors during debug logging
        console.log('[SessionPersistence] AUTO-SAVE BEFORE - Could not parse existing data (may be first save)');
      }
      
      const updatedSession = {
        ...currentSession,
        lastSaved: new Date().toISOString()
      };
      
      try {
        // Use secure encrypted storage
        await secureStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(updatedSession));
        console.log('[SessionPersistence] AUTO-SAVE COMPLETE - Session saved successfully with encryption', {
          completedTasks: updatedSession.completedTasks,
          saveTimestamp: updatedSession.lastSaved
        });
        
        // Verify what was actually saved with decryption
        const afterStorage = await secureStorage.getItem(SESSION_STORAGE_KEY);
        if (afterStorage) {
          const afterParsed = JSON.parse(afterStorage);
          console.log('[SessionPersistence] AUTO-SAVE AFTER - encrypted storage completedTasks:', afterParsed.completedTasks);
        }
      } catch (error) {
        console.error('[SessionPersistence] Failed to save session:', error);
        
        // Fallback to unencrypted if absolutely necessary
        console.warn('[SessionPersistence] Attempting fallback to unencrypted storage');
        try {
          localStorage.setItem(`fallback-${SESSION_STORAGE_KEY}`, JSON.stringify(updatedSession));
          console.log('[SessionPersistence] Fallback save successful');
        } catch (fallbackError) {
          console.error('[SessionPersistence] Failed to save session even with fallback:', fallbackError);
        }
      }
    }, 1000); // 1 second debounce for faster testing

    return () => clearTimeout(saveTimer);
  }, [session.taskProgress, session.completedTasks, session.currentStep, isLoading]); // Only save on meaningful changes

  // Session expiration scheduling - ONLY when user is authenticated
  useEffect(() => {
    const hasAuthToken = !!localStorage.getItem('account-token');

    if (isLoading || !session.onboardingComplete || !hasAuthToken) {
      clearSessionTimers();

      if (!hasAuthToken) {
        setSessionExpired(false);
        setShowExpirationWarning(false);
      }

      return;
    }

    const now = Date.now();
    const lastActivityMs = new Date(session.lastActivity).getTime();
    const sessionStartMs = new Date(session.sessionStarted).getTime();

    const idleElapsed = now - lastActivityMs;
    const totalElapsed = now - sessionStartMs;

    const idleWarningDelay = SESSION_CONFIG.WARNING_TIME - idleElapsed;
    const idleExpirationDelay = SESSION_CONFIG.MAX_IDLE_TIME - idleElapsed;
    const absoluteExpirationDelay = SESSION_CONFIG.MAX_SESSION_TIME - totalElapsed;

    console.log('[SessionPersistence] Scheduling session timers:', {
      idleElapsedMs: idleElapsed,
      totalElapsedMs: totalElapsed,
      idleWarningDelay,
      idleExpirationDelay,
      absoluteExpirationDelay,
    });

    clearSessionTimers();

    // Already expired: force the expired state immediately
    if (idleExpirationDelay <= 0 || absoluteExpirationDelay <= 0) {
      console.warn('[SessionPersistence] Session already expired when scheduling timers');
      setShowExpirationWarning(false);
      setSessionExpired(true);
      return;
    }

    // Show warning immediately if already within warning window,
    // otherwise schedule it for the precise time.
    if (idleWarningDelay <= 0) {
      setShowExpirationWarning(true);
    } else {
      warningTimerRef.current = window.setTimeout(() => {
        if (!localStorage.getItem('account-token')) return;
        console.log('[SessionPersistence] Warning timer fired');
        setShowExpirationWarning(true);
      }, idleWarningDelay);
    }

    // Inactivity expiration timer
    expirationTimerRef.current = window.setTimeout(() => {
      if (!localStorage.getItem('account-token')) return;
      console.warn('[SessionPersistence] Inactivity expiration timer fired');
      setShowExpirationWarning(false);
      setSessionExpired(true);
    }, idleExpirationDelay);

    // Absolute max session timer
    absoluteSessionTimerRef.current = window.setTimeout(() => {
      if (!localStorage.getItem('account-token')) return;
      console.warn('[SessionPersistence] Absolute session expiration timer fired');
      setShowExpirationWarning(false);
      setSessionExpired(true);
    }, absoluteExpirationDelay);

    return () => {
      clearSessionTimers();
    };
  }, [
    session.lastActivity,
    session.sessionStarted,
    session.onboardingComplete,
    isLoading,
    clearSessionTimers,
  ]);

  // Keep a live countdown for the warning modal
  useEffect(() => {
    const hasAuthToken = !!localStorage.getItem('account-token');

    if (isLoading || !session.onboardingComplete || !hasAuthToken) {
      setWarningTimeRemainingMs(SESSION_CONFIG.WARNING_TIME);
      return;
    }

    const updateRemainingTime = () => {
      const now = Date.now();
      const lastActivityMs = new Date(session.lastActivity).getTime();
      const idleElapsed = now - lastActivityMs;
      const remaining = Math.max(0, SESSION_CONFIG.MAX_IDLE_TIME - idleElapsed);

      setWarningTimeRemainingMs(remaining);
    };

    updateRemainingTime();

    const interval = window.setInterval(updateRemainingTime, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [session.lastActivity, session.onboardingComplete, isLoading]);

  // Update last activity on meaningful user interaction
  useEffect(() => {
    if (isLoading || !session.onboardingComplete) return;

    let lastRecordedAt = 0;

    const updateActivity = () => {
      if (!localStorage.getItem('account-token')) return;

      const now = Date.now();

      // Throttle to avoid hammering state/storage during bursts of activity
      if (now - lastRecordedAt < 15000) {
        return;
      }

      lastRecordedAt = now;

      setSession(prev => ({
        ...prev,
        lastActivity: new Date(now).toISOString()
      }));

      setShowExpirationWarning(false);
    };

    const events = ['click', 'keydown', 'scroll', 'touchstart', 'mousedown'];

    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [isLoading, session.onboardingComplete]);

  // Update user information
  const updateUserInfo = useCallback(async (info: {
    email?: string;
    name?: string;
    phone?: string;
    roles?: string[];
    notifications?: {
      email: boolean;
      inApp: boolean;
      sms: boolean;
    };
    accountId?: string;
    accountToken?: string;
  }) => {
    console.log('[SessionPersistence] Updating user info:', info);

    const now = new Date().toISOString();
    const newSession = {
      ...session,
      ...info,
      onboardingComplete: true,
      currentStep: 'dashboard' as const,
      sessionStarted: now,  // Reset session start time on login
      lastActivity: now,     // Reset last activity on login
      lastSaved: now
    };

    try {
      // Save to encrypted storage
      await secureStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
      console.log('[SessionPersistence] User info saved to encrypted storage with fresh session timestamps');

      // Update React state
      setSession(newSession);

      // Clear any session expiration warnings
      setSessionExpired(false);
      setShowExpirationWarning(false);

      // Notify other hook instances (e.g. App) to sync the updated session
      // so expiration timers can start with the correct onboardingComplete state
      window.dispatchEvent(new Event('session-updated'));
    } catch (error) {
      console.error('[SessionPersistence] Failed to save user info:', error);
      throw error;
    }
  }, []); // removed session here

  // Login function - used after MFA verification
  const login = useCallback(async (token: string, user: { id: string; email: string; name: string; phone?: string }) => {
    console.log('[SessionPersistence] Login called with user:', user.email);
    
    // Store token in localStorage for API calls
    localStorage.setItem('account-token', token);
    
    // Update session with user info
    await updateUserInfo({
      email: user.email,
      name: user.name,
      phone: user.phone,
      accountId: user.id,
      accountToken: token
    });
    
    console.log('[SessionPersistence] Login complete');
  }, [updateUserInfo]);

  // Update current step
  const updateCurrentStep = useCallback((step: UserSession['currentStep']) => {
    setSession(prev => ({
      ...prev,
      currentStep: step
    }));
  }, []);

  // Start tracking a section (when user begins)
  const startSection = useCallback((taskId: string) => {
    setSession(prev => ({
      ...prev,
      sectionStartTimes: {
        ...prev.sectionStartTimes,
        [taskId]: prev.sectionStartTimes[taskId] || new Date().toISOString()
      }
    }));
  }, []);

  // Update task progress - ASYNC WITH IMMEDIATE SAVE AND BACKEND SYNC
  const updateTaskProgress = useCallback(async (taskId: string, answers: Record<string, unknown>, status?: 'not-started' | 'in-progress' | 'complete'): Promise<void> => {
    console.log('[SessionPersistence] updateTaskProgress called:', { taskId, answers, status });
    console.log('[SessionPersistence] Current session before update:', session);
    const base = sessionRef.current;
    const currentProgress = base.taskProgress[taskId] || {
      status: 'not-started' as const,
      answers: {},
      lastUpdated: new Date().toISOString()
    };
    
    console.log('[SessionPersistence] Current progress for task:', currentProgress);

    const updatedProgress = {
      ...currentProgress,
      answers: { ...currentProgress.answers, ...answers },
      status: status || currentProgress.status,
      lastUpdated: new Date().toISOString()
    };

    console.log('[SessionPersistence] Updated progress:', updatedProgress);

    const newSession = {
      ...base,
      taskProgress: {
        ...base.taskProgress,
        [taskId]: updatedProgress
      },
      lastSaved: new Date().toISOString()
    };

    console.log('[SessionPersistence] New session state:', newSession);

    try {
      // IMMEDIATE SAVE TO ENCRYPTED STORAGE TO PREVENT DATA LOSS
      await secureStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
      console.log('[SessionPersistence] IMMEDIATE SAVE SUCCESSFUL - Data saved to encrypted storage');
      
      // Update React state after successful save
      setSession(newSession);
      
      // Verify the save worked
      const verification = await secureStorage.getItem(SESSION_STORAGE_KEY);
      if (verification) {
        const parsed = JSON.parse(verification);
        console.log('[SessionPersistence] SAVE VERIFICATION - Data confirmed in encrypted storage:', {
          taskId,
          hasTaskProgress: !!parsed.taskProgress,
          hasSpecificTask: !!parsed.taskProgress?.[taskId],
          taskData: parsed.taskProgress?.[taskId]
        });

        // BACKEND TRANSMISSION - Only if user has account token
        const accountToken = localStorage.getItem('account-token');
        if (accountToken) { // ENABLED: API calls for server data backup
          console.log('[SessionPersistence] Transmitting data to backend for cross-device sync via reconciliation...');

          // Reconcile session data with backend to ensure taskProgress is persisted
          apiService.reconcileUserData(newSession)
            .then((reconciledSession) => {
              console.log('[SessionPersistence] Ã¢Å“â€¦ Backend sync successful for task via reconciliation:', taskId);
              console.log('[SessionPersistence] Reconciled task progress:', reconciledSession.taskProgress?.[taskId]);
            })
            .catch((error) => {
              console.warn('[SessionPersistence] Ã¢Å¡Â Ã¯Â¸Â Backend reconciliation failed (non-critical):', error);
            });
        } else {
          console.log('[SessionPersistence] No account token - skipping backend sync');
        }
      } else {
        console.error('[SessionPersistence] SAVE VERIFICATION FAILED - Data not found in encrypted storage');
        throw new Error('Save verification failed');
      }
    } catch (error) {
      console.error('[SessionPersistence] IMMEDIATE SAVE FAILED:', error);
      throw error;
    }
  }, []); // removed session here

  // Complete a task - WITH IMMEDIATE SAVE AND BACKEND SYNC
  const completeTask = useCallback(
    async (
      taskId: string,
      meta?: UserSession['taskProgress'][string]['completionMeta']
    ) => {
      console.log('[SessionPersistence] ============== TASK COMPLETION START ==============');
      console.log('[SessionPersistence] completeTask called for:', taskId);
      console.log('[SessionPersistence] Completion meta:', meta);
      console.log('[SessionPersistence] Current completed tasks:', sessionRef.current.completedTasks);
      console.trace('[completeTask trace]');

      const currentSession = sessionRef.current;

      if (currentSession.completedTasks.includes(taskId)) {
        console.log('[SessionPersistence] Task already completed:', taskId);
        return;
      }

      trackTaskComplete(taskId);
      console.log('[SessionPersistence] Task not yet completed, proceeding...');

      const prevProgress = currentSession.taskProgress[taskId] || {
        status: 'not-started' as const,
        answers: {},
        lastUpdated: new Date().toISOString(),
      };

      const newProgress = {
        ...prevProgress,
        status: 'complete' as const,
        lastUpdated: new Date().toISOString(),
        completionMeta: {
          ...(prevProgress as unknown).completionMeta,
          ...(meta ?? {}),
          completedAt: meta?.completedAt ?? new Date().toISOString(),
        },
      };

      const newSession = {
        ...currentSession,
        completedTasks: [...currentSession.completedTasks, taskId],
        taskProgress: {
          ...currentSession.taskProgress,
          [taskId]: newProgress,
        },
        lastSaved: new Date().toISOString(),
      };

      try {
        console.log('[SessionPersistence] Saving completed task to storage...', { taskId, completedTasks: newSession.completedTasks });
        await secureStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
        
        console.log('[SessionPersistence] Updating React state...');
        setSession(newSession);
        sessionRef.current = newSession;

        console.log('[SessionPersistence] Verifying save...');
        const verification = await secureStorage.getItem(SESSION_STORAGE_KEY);
        if (verification) {
          const parsed = JSON.parse(verification);
          console.log('[SessionPersistence] Ã¢Å“â€¦ Completion verified in storage:', {
            taskId,
            completedTasks: parsed.completedTasks,
            isIncluded: parsed.completedTasks.includes(taskId)
          });

          const accountToken = localStorage.getItem('account-token');
          if (accountToken) {
            console.log('[SessionPersistence] Syncing to backend...');
            apiService.reconcileUserData(newSession).catch((error) => {
              console.warn('[SessionPersistence] Ã¢Å¡Â Ã¯Â¸Â Task completion reconciliation failed (non-critical):', error);
            });
          }
        } else {
          console.error('[SessionPersistence] Ã¢ÂÅ’ Completion verification failed - nothing in storage');
          throw new Error('Completion verification failed');
        }

        console.log('[SessionPersistence] ============== TASK COMPLETION SUCCESS ==============');
      } catch (error) {
        console.error('[SessionPersistence] Ã¢ÂÅ’ TASK COMPLETION SAVE FAILED:', error);
        throw error;
      }
    },
    [session] // you already have this; keep it as-is for now
  );



  const uncompleteTask = useCallback(
    async (
      taskId: string,
      meta?: UserSession['taskProgress'][string]['completionMeta'] & {
        uncompleteReason?: string;
      }
    ) => {
      const currentSession = sessionRef.current;

      if (!currentSession.completedTasks.includes(taskId)) return;

      const prevProgress = currentSession.taskProgress?.[taskId] ?? {
        status: 'not-started' as const,
        answers: {},
        lastUpdated: new Date().toISOString(),
      };

      const nextProgress = {
        ...prevProgress,
        status: 'in-progress' as const,
        lastUpdated: new Date().toISOString(),
        completionMeta: {
          ...(prevProgress as unknown).completionMeta,
          ...(meta ?? {}),
          uncompletedAt: new Date().toISOString(),
        },
      };

      const nextSession: UserSession = {
        ...currentSession,
        completedTasks: currentSession.completedTasks.filter((t) => t !== taskId),
        taskProgress: {
          ...currentSession.taskProgress,
          [taskId]: nextProgress,
        },
        lastSaved: new Date().toISOString(),
      };

      await secureStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(nextSession));
      setSession(nextSession);

      const accountToken = localStorage.getItem('account-token');
      if (accountToken) {
        apiService.reconcileUserData(nextSession).catch((error) => {
          console.warn('[SessionPersistence] Ã¢Å¡Â Ã¯Â¸Â Uncomplete reconciliation failed (non-critical):', error);
        });
      }
    },
    []
  );

  const reconcileFlowCompletion = useCallback(
    async (flowId: FlowId) => {
      const cfg = FLOW_COMPLETION_CONFIG[flowId];
      if (!cfg) return;

      const completeTaskId = cfg.completeTaskId;
      const s = sessionRef.current;

      console.log('[SessionPersistence] reconcileFlowCompletion called:', { flowId, completeTaskId, isCompleted: s.completedTasks.includes(completeTaskId) });

      if (!s.completedTasks.includes(completeTaskId)) return;

      const hasMeaningful = cfg.hasMeaningfulData(s);
      const meta = s.taskProgress?.[completeTaskId]?.completionMeta;
      const completionKind = meta?.completionKind;

      console.log('[SessionPersistence] reconcileFlowCompletion check:', { 
        flowId, 
        hasMeaningful, 
        completionKind,
        willUncomplete: !hasMeaningful && completionKind !== 'empty_flow'
      });

      if (hasMeaningful) return;

      // Only "empty_flow" completion should remain complete while empty.
      if (completionKind === 'empty_flow') {
        console.log('[SessionPersistence] Keeping empty_flow completion:', { flowId, completeTaskId });
        return;
      }

      console.log('[SessionPersistence] Ã¢Å¡Â Ã¯Â¸Â Uncompleting task due to no meaningful data and not empty_flow:', { flowId, completeTaskId });
      await uncompleteTask(completeTaskId, {
        source: 'useSessionPersistence',
        flowId,
        uncompleteReason: 'flow_became_empty',
      });
    },
    [uncompleteTask]
  );


  /**
   * Default emptiness heuristic:
   * A task is "empty" if it has no answers object or it has no keys.
   * (Flow-specific "meaningful" logic lives in FLOW_COMPLETION_CONFIG.)
   */
  const isTaskEmptyForCompletion = useCallback((taskId: string): boolean => {
    const progress = sessionRef.current.taskProgress?.[taskId];
    const answers = progress?.answers;

    if (!answers || typeof answers !== 'object') return true;

    // Special-case wrappers (optional but explicit)
    if (taskId === 'legal_documents') {
      const docs = (answers as unknown)?.documents;
      return !(Array.isArray(docs) && docs.length > 0);
    }

    if (taskId === 'executors') {
      const execs = (answers as unknown)?.selectedExecutors;
      return !(Array.isArray(execs) && execs.length > 0);
    }
    // Generic deep check (treats [] and {} as empty)
    return !hasMeaningfulValue(answers);
  }, [hasMeaningfulValue]);

  /**
   * Completion intent gate:
   * - If confirmEmpty=false and the task is "empty", we do NOT complete it.
   * - If confirmEmpty=true, we allow completion even if empty.
   */
  const requestCompleteTask = useCallback(
    async (taskId: string, opts?: RequestCompleteTaskOptions) => {
      const confirmEmpty = !!opts?.confirmEmpty;

      console.log('[SessionPersistence] requestCompleteTask called:', { taskId, confirmEmpty, meta: opts?.meta });
      console.trace('[requestCompleteTask trace]');

      if (!confirmEmpty) {
        const empty = isTaskEmptyForCompletion(taskId);
        console.log('[SessionPersistence] requestCompleteTask emptiness check:', { taskId, empty });
        if (empty) {
          console.log('[SessionPersistence] requestCompleteTask BLOCKED (empty + not confirmed)', { taskId });
          return;
        }
      } else {
        console.log('[SessionPersistence] requestCompleteTask confirmEmpty=true, proceeding with completion', { taskId });
      }

      console.log('[SessionPersistence] Calling completeTask for:', taskId);
      await completeTask(taskId, {
        ...(opts?.meta ?? {}),
        confirmedEmpty: confirmEmpty,
      });
      console.log('[SessionPersistence] completeTask finished for:', taskId);
    },
    [completeTask, isTaskEmptyForCompletion]
  );


  // Check if a task is complete
  const isTaskComplete = useCallback((taskId: string) => {
    const isComplete = session.completedTasks.includes(taskId);
    console.log('[SessionPersistence] isTaskComplete check:', {
      taskId,
      isComplete,
      completedTasks: session.completedTasks,
      sessionRefCompletedTasks: sessionRef.current.completedTasks
    });
    return isComplete;
  }, [session.completedTasks]);

  // Get task progress
  const getTaskProgress = useCallback((taskId: string) => {
    return session.taskProgress[taskId] || {
      status: 'not-started' as const,
      answers: {},
      lastUpdated: new Date().toISOString()
    };
  }, [session.taskProgress]);

  // Mark section as transmitted
  const markTransmitted = useCallback((taskId: string) => {
    setSession(prev => ({
      ...prev,
      transmissionStatus: {
        ...prev.transmissionStatus,
        [taskId]: true
      }
    }));
  }, []);

  // Check if section has been transmitted
  const isTransmitted = useCallback((taskId: string) => {
    return session.transmissionStatus[taskId] || false;
  }, [session.transmissionStatus]);

  // Clear session (for logout)
  const clearSession = useCallback(() => {
    setSession(createDefaultSession());
    setSessionExpired(false);
    setShowExpirationWarning(false);
    
    // Clear from storage
    try {
      secureStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('[SessionPersistence] Failed to clear session from storage:', error);
    }

  }, []);

  // Logout session (preserve estate planning data, clear authentication)
  const logoutSession = useCallback(async () => {
    console.log('[SessionPersistence] Logout - preserving estate planning data...');

    clearSessionTimers();

    // Clear auth tokens
    localStorage.removeItem('account-token');
    localStorage.removeItem('account-refresh-token');
    localStorage.removeItem('refreshToken');

    const base = sessionRef.current;

    const newSession = {
      ...base,
      email: undefined,
      name: undefined,
      accountId: undefined,
      accountToken: undefined,
      currentStep: 'landing' as const,
      lastSaved: new Date().toISOString(),
    };

    try {
      await secureStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
      console.log('[SessionPersistence] Logout state saved to encrypted storage');

      setSession(newSession);
      sessionRef.current = newSession;
      setShowExpirationWarning(false);

      // Notify other hook instances so they clear timers too
      window.dispatchEvent(new Event('session-updated'));

      console.log('[SessionPersistence] Logout complete - user data preserved');
    } catch (error) {
      console.error('[SessionPersistence] Failed to save logout state:', error);
      throw error;
    }
  }, [clearSessionTimers]);

  // Extend session (when user is active)
  const extendSession = useCallback(() => {
    const now = new Date().toISOString();

    setSession(prev => ({
      ...prev,
      lastActivity: now,
      sessionStarted: now  // Also reset absolute timer so extend actually works
    }));

    setWarningTimeRemainingMs(SESSION_CONFIG.MAX_IDLE_TIME);
    setSessionExpired(false);
    setShowExpirationWarning(false);
  }, []);

  // Sync contacts from backend to session
  const syncContactsFromBackend = useCallback(async () => {
    if (!session.accountId || !session.accountToken) {
      console.log('[SessionPersistence] Skipping contact sync - not authenticated');
      return;
    }

    try {
      console.log('[SessionPersistence] Syncing contacts from backend...');
      const contacts = await unifiedContactService.getAllContacts(true); // Force refresh

      setSession(prev => ({
        ...prev,
        contacts
      }));

      console.log(`[SessionPersistence] Synced ${contacts.length} contacts to session`);
    } catch (error) {
      console.error('[SessionPersistence] Failed to sync contacts from backend:', error);
      // Don't throw - this is non-critical
    }
  }, [session.accountId, session.accountToken]);

  // Get contacts from session (with fallback to backend)
  const getContacts = useCallback(async (): Promise<UnifiedContact[]> => {
    // Return session contacts if available
    if (session.contacts && session.contacts.length > 0) {
      return session.contacts;
    }

    // Otherwise fetch from backend
    if (session.accountId && session.accountToken) {
      try {
        const contacts = await unifiedContactService.getAllContacts();

        // Update session
        setSession(prev => ({
          ...prev,
          contacts
        }));

        return contacts;
      } catch (error) {
        console.error('[SessionPersistence] Failed to fetch contacts:', error);
        return [];
      }
    }

    return [];
  }, [session.contacts, session.accountId, session.accountToken]);

  return {
    session,
    isLoading,
    encryptionStatus,
    sessionExpired,
    showExpirationWarning,
    warningTimeRemainingMs,
    syncStatus,
    login,
    updateUserInfo,
    updateCurrentStep,
    startSection,
    updateTaskProgress,
    completeTask,
    requestCompleteTask,
    isTaskEmptyForCompletion,
    uncompleteTask,
    reconcileFlowCompletion,
    isTaskComplete,
    getTaskProgress,
    markTransmitted,
    isTransmitted,
    clearSession,
    logoutSession,
    extendSession,
    syncContactsFromBackend,
    getContacts,
  };
};